import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { colors, spacing, shadows } from '../theme/index';
import API_BASE_URL from '../config/api';
import { EMPTY_INVOICE_FORM, TONES, addDaysToDateString, formatCurrency, parseDateString, todayInputValue } from './financeUtils';

// Add / edit form for a purchase invoice (credit or cash bill) or a
// distribution invoice. The same component serves both modules - only the
// labels, the date field name and the cash-bill support come from the module
// configuration.
function FinanceInvoiceModal({ config, invoice, presetParty, presetBillType, parties, onClose, onSaved }) {
  const isEdit = Boolean(invoice && invoice.id);
  const [form, setForm] = useState(EMPTY_INVOICE_FORM);
  const [billType, setBillType] = useState('credit');
  const [autoDueDate, setAutoDueDate] = useState('');
  const [partyName, setPartyName] = useState('');
  const [partyId, setPartyId] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState('');

  useEffect(() => {
    setAutoDueDate('');
    if (isEdit) {
      setForm({
        invoiceNumber: invoice.invoiceNumber || '',
        amount: invoice.amount === null || invoice.amount === undefined ? '' : String(invoice.amount),
        paidAmount: invoice.paidAmount > 0 ? String(invoice.paidAmount) : '',
        invoiceDate: invoice[config.dateField] || '',
        paymentStatus: invoice.paymentStatus || 'unpaid',
        dueDate: invoice.dueDate || '',
        notes: invoice.notes || '',
      });
      setPartyName(invoice.partyName || '');
      setPartyId(invoice.partyId || null);
      setBillType(invoice.paymentMethod === 'cash' ? 'cash' : 'credit');
    } else {
      setForm({ ...EMPTY_INVOICE_FORM, invoiceDate: todayInputValue() });
      setPartyName(presetParty ? presetParty.name : '');
      setPartyId(presetParty ? presetParty.id : null);
      setBillType(presetBillType === 'cash' ? 'cash' : 'credit');
    }
  }, [invoice, isEdit, config.dateField, presetParty, presetBillType]);

  const suggestions = useMemo(() => {
    const term = partyName.trim().toLowerCase();
    const list = Array.isArray(parties) ? parties : [];
    const matched = term ? list.filter((party) => party.name.toLowerCase().includes(term)) : list;
    return matched.slice(0, 6);
  }, [parties, partyName]);

  const suggestionsExactMatch = suggestions.some(
    (party) => party.name.toLowerCase() === partyName.trim().toLowerCase()
  );

  // Cash bills (purchases only): settled in cash at purchase time, no due date.
  const supportsCashBills = Boolean(config.hasCashBills);
  const isCashBill = supportsCashBills && billType === 'cash';

  // Credit period of the selected distributor (e.g. 30 / 45 days).
  const selectedParty = useMemo(
    () => (Array.isArray(parties) ? parties.find((party) => party.id === partyId) : null),
    [parties, partyId]
  );
  const creditDays = selectedParty && Number(selectedParty.creditDays) > 0 ? Number(selectedParty.creditDays) : 0;

  // Default due date = invoice date + distributor credit days. The field is
  // only filled while it is empty or still holds the automatic value, so a
  // manually typed due date is never overwritten.
  useEffect(() => {
    if (!supportsCashBills || isCashBill || creditDays <= 0 || !form.invoiceDate) return;
    if (form.dueDate && form.dueDate !== autoDueDate) return;
    const due = addDaysToDateString(form.invoiceDate, creditDays);
    if (!due || due === form.dueDate) return;
    setAutoDueDate(due);
    setForm((current) => ({ ...current, dueDate: due }));
  }, [supportsCashBills, isCashBill, creditDays, form.invoiceDate, form.dueDate, autoDueDate]);

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: '' }));
    setServerError('');
  };

  const handlePartyNameChange = (value) => {
    setPartyName(value);
    setShowSuggestions(true);
    setErrors((current) => ({ ...current, partyName: '' }));
    setServerError('');
    const match = (parties || []).find((party) => party.name.toLowerCase() === value.trim().toLowerCase());
    setPartyId(match ? match.id : null);
  };

  const pickSuggestion = (party) => {
    setPartyName(party.name);
    setPartyId(party.id);
    setShowSuggestions(false);
    setErrors((current) => ({ ...current, partyName: '' }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!partyName.trim()) nextErrors.partyName = config.partyTitle + ' name is required';
    if (!form.invoiceNumber.trim()) nextErrors.invoiceNumber = 'Invoice number is required';

    const amount = parseFloat(String(form.amount).replace(/,/g, ''));
    if (!Number.isFinite(amount) || amount <= 0) nextErrors.amount = 'Enter an amount greater than 0';

    // Cash bills are always fully paid, so there is nothing else to validate.
    if (isCashBill) {
      if (!form.invoiceDate) nextErrors.invoiceDate = config.dateLabel + ' is required';
      setErrors(nextErrors);
      return Object.keys(nextErrors).length === 0;
    }

    const paidRaw = String(form.paidAmount).trim();
    const paid = paidRaw === '' ? 0 : parseFloat(paidRaw.replace(/,/g, ''));
    if (paidRaw !== '' && (!Number.isFinite(paid) || paid < 0)) {
      nextErrors.paidAmount = 'Enter a valid paid amount (0 or more)';
    } else if (Number.isFinite(amount) && paid > amount) {
      nextErrors.paidAmount = 'Paid amount cannot be more than the invoice amount';
    }

    if (!form.invoiceDate) nextErrors.invoiceDate = config.dateLabel + ' is required';

    // A due date is required while anything is still to be paid.
    if (Number.isFinite(amount) && paid < amount) {
      const due = parseDateString(form.dueDate);
      const bill = parseDateString(form.invoiceDate);
      if (!form.dueDate) {
        nextErrors.dueDate = 'Due date is required when there is a balance to pay';
      } else if (due && bill && due < bill) {
        nextErrors.dueDate = 'Due date cannot be before the ' + config.dateLabel.toLowerCase();
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setServerError('');
    if (!validate()) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const amount = parseFloat(String(form.amount).replace(/,/g, ''));
      const paidRaw = String(form.paidAmount).trim();
      const paidAmount = isCashBill ? amount : (paidRaw === '' ? 0 : parseFloat(paidRaw.replace(/,/g, '')));
      const derivedStatus = isCashBill ? 'paid' : (paidAmount >= amount ? 'paid' : (paidAmount > 0 ? 'partial' : 'unpaid'));

      const payload = {
        invoiceNumber: form.invoiceNumber.trim(),
        amount,
        paidAmount,
        paymentStatus: derivedStatus,
        dueDate: isCashBill ? null : (derivedStatus === 'paid' ? (form.dueDate || null) : form.dueDate),
        notes: form.notes,
      };
      if (supportsCashBills) {
        payload.paymentMethod = isCashBill ? 'cash' : 'credit';
      }
      payload[config.dateField] = form.invoiceDate;
      payload[config.partyNameField] = partyName.trim();
      payload[config.partyIdField] = partyId || null;

      const url = API_BASE_URL + '/api/admin/finance/' + config.endpoint + (isEdit ? '/' + invoice.id : '');
      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setServerError(data.message || 'Could not save the invoice. Please try again.');
        setSaving(false);
        return;
      }

      const savedMessage = isEdit
        ? config.invoiceTitle + ' invoice updated successfully'
        : config.invoiceTitle + ' invoice added successfully';
      onSaved(savedMessage, data.data);
    } catch (error) {
      setServerError('Connection error: ' + error.message);
      setSaving(false);
    }
  };

  // Live amount paid / balance preview for the form.
  const formPaidRaw = String(form.paidAmount).trim();
  const formPaidValue = formPaidRaw === '' ? 0 : parseFloat(formPaidRaw.replace(/,/g, ''));
  const formPaid = Number.isFinite(formPaidValue) && formPaidValue > 0 ? formPaidValue : 0;
  const formAmount = parseFloat(String(form.amount).replace(/,/g, '')) || 0;
  const formBalance = Math.max(Math.round((formAmount - formPaid) * 100) / 100, 0);
  const formStatus = formPaid >= formAmount && formAmount > 0 ? 'paid' : (formPaid > 0 ? 'partial' : 'unpaid');

  const inputStyle = {
    width: '100%',
    padding: spacing.md,
    border: '2px solid ' + colors.border,
    borderRadius: '0.5rem',
    fontSize: '0.95rem',
    boxSizing: 'border-box',
    outline: 'none',
    fontFamily: 'inherit',
  };
  const labelStyle = {
    display: 'block',
    fontWeight: 700,
    fontSize: '0.85rem',
    color: colors.text.primary,
    marginBottom: spacing.xs,
  };
  const errorStyle = { color: colors.error, fontSize: '0.8rem', marginTop: spacing.xs, marginBottom: 0 };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 60,
        padding: spacing.md,
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.96, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.2 }}
        onClick={(event) => event.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '1rem',
          boxShadow: shadows.xl,
          width: '100%',
          maxWidth: '640px',
          maxHeight: '92vh',
          overflowY: 'auto',
        }}
      >
        <div
          style={{
            padding: spacing.lg,
            borderBottom: '2px solid ' + colors.border,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: spacing.md,
          }}
        >
          <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: colors.text.primary }}>
            {(isEdit ? 'Edit ' : 'Add ') + (isCashBill ? 'Cash Bill' : config.invoiceTitle)}
          </h3>
          <button
            type='button'
            onClick={onClose}
            aria-label='Close'
            style={{ border: 'none', background: 'transparent', fontSize: '1.4rem', cursor: 'pointer', color: colors.text.secondary }}
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: spacing.lg }}>
          {supportsCashBills ? (
            <div style={{ marginBottom: spacing.lg }}>
              <label style={labelStyle}>Bill Type *</label>
              <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
                {[
                  { value: 'credit', label: 'Credit Bill (due later)' },
                  { value: 'cash', label: 'Cash Bill (paid now)' },
                ].map((option) => {
                  const active = billType === option.value;
                  const palette = option.value === 'cash' ? TONES.paid : TONES.partial;
                  return (
                    <button
                      key={option.value}
                      type='button'
                      onClick={() => {
                        setBillType(option.value);
                        setErrors((current) => ({ ...current, paidAmount: '', dueDate: '' }));
                        setServerError('');
                      }}
                      style={{
                        flex: '1 1 220px',
                        padding: spacing.sm + ' ' + spacing.md,
                        borderRadius: '0.5rem',
                        border: '2px solid ' + (active ? palette.border : colors.border),
                        backgroundColor: active ? palette.background : 'white',
                        color: active ? palette.color : colors.text.primary,
                        fontWeight: 800,
                        fontSize: '0.85rem',
                        cursor: 'pointer',
                      }}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
              <p
                style={{
                  color: isCashBill ? TONES.paid.color : colors.text.secondary,
                  fontSize: '0.78rem',
                  fontWeight: isCashBill ? 700 : 400,
                  marginTop: spacing.xs,
                  marginBottom: 0,
                }}
              >
                {isCashBill
                  ? 'Cash bill: the full amount is recorded as paid at purchase - no due date.'
                  : 'Credit bill: record what is paid now - the balance stays due on the due date.'}
              </p>
            </div>
          ) : null}
          <div className='pd-form-grid'>

            <div style={{ position: 'relative' }}>
              <label style={labelStyle} htmlFor='pd-party-name'>
                {config.partyTitle} *
              </label>
              <input
                id='pd-party-name'
                type='text'
                value={partyName}
                onChange={(event) => handlePartyNameChange(event.target.value)}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                placeholder={'Search or type a new ' + config.partyTitle.toLowerCase()}
                style={inputStyle}
                autoComplete='off'
              />
              {errors.partyName ? <p style={errorStyle}>{errors.partyName}</p> : null}
              {showSuggestions && suggestions.length > 0 ? (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid ' + colors.border,
                    borderRadius: '0.5rem',
                    boxShadow: shadows.lg,
                    zIndex: 5,
                    maxHeight: '180px',
                    overflowY: 'auto',
                  }}
                >
                  {suggestions.map((party) => (
                    <button
                      key={party.id}
                      type='button'
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => pickSuggestion(party)}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        padding: spacing.sm + ' ' + spacing.md,
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        color: colors.text.primary,
                      }}
                    >
                      {party.name}
                    </button>
                  ))}
                </div>
              ) : null}
              {partyName.trim() && !partyId && !suggestionsExactMatch ? (
                <p style={{ color: colors.text.secondary, fontSize: '0.78rem', marginTop: spacing.xs, marginBottom: 0 }}>
                  New {config.partyTitle.toLowerCase()} "{partyName.trim()}" will be saved automatically.
                </p>
              ) : null}
            </div>

            <div>
              <label style={labelStyle} htmlFor='pd-invoice-number'>
                Invoice Number *
              </label>
              <input
                id='pd-invoice-number'
                type='text'
                value={form.invoiceNumber}
                onChange={(event) => setField('invoiceNumber', event.target.value)}
                placeholder='e.g. INV-2026-001'
                style={inputStyle}
                autoComplete='off'
              />
              {errors.invoiceNumber ? <p style={errorStyle}>{errors.invoiceNumber}</p> : null}
            </div>

            <div>
              <label style={labelStyle} htmlFor='pd-amount'>
                {config.amountLabel} * (INR)
              </label>
              <input
                id='pd-amount'
                type='number'
                min='0'
                step='0.01'
                value={form.amount}
                onChange={(event) => setField('amount', event.target.value)}
                placeholder='0.00'
                style={inputStyle}
              />
              {errors.amount ? <p style={errorStyle}>{errors.amount}</p> : null}
              {!errors.amount && parseFloat(form.amount) > 0 ? (
                <p style={{ color: colors.text.secondary, fontSize: '0.78rem', marginTop: spacing.xs, marginBottom: 0 }}>
                  {formatCurrency(parseFloat(form.amount))}
                </p>
              ) : null}
            </div>

            {!isCashBill ? (
            <div>
              <label style={labelStyle} htmlFor='pd-paid-amount'>
                Amount Paid {form.paidAmount !== '' && parseFloat(form.paidAmount) > 0 ? '' : '(leave empty if nothing paid yet)'}
              </label>
              <input
                id='pd-paid-amount'
                type='number'
                min='0'
                step='0.01'
                value={form.paidAmount}
                onChange={(event) => setField('paidAmount', event.target.value)}
                placeholder='0.00'
                style={inputStyle}
              />
              {errors.paidAmount ? <p style={errorStyle}>{errors.paidAmount}</p> : null}
              {!errors.paidAmount && formBalance > 0 && formPaid > 0 ? (
                <p style={{ color: TONES.partial.color, fontSize: '0.78rem', fontWeight: 700, marginTop: spacing.xs, marginBottom: 0 }}>
                  Partly paid &middot; balance {formatCurrency(formBalance)}
                </p>
              ) : null}
              {!errors.paidAmount && formBalance <= 0 && formPaid > 0 ? (
                <p style={{ color: TONES.paid.color, fontSize: '0.78rem', fontWeight: 700, marginTop: spacing.xs, marginBottom: 0 }}>
                  Fully paid
                </p>
              ) : null}
            </div>
            ) : null}

            <div>
              <label style={labelStyle} htmlFor='pd-invoice-date'>
                {config.dateLabel} *
              </label>
              <input
                id='pd-invoice-date'
                type='date'
                value={form.invoiceDate}
                onChange={(event) => setField('invoiceDate', event.target.value)}
                style={inputStyle}
              />
              {errors.invoiceDate ? <p style={errorStyle}>{errors.invoiceDate}</p> : null}
            </div>

            {!isCashBill ? (
            <div>
              <label style={labelStyle}>Payment Status *</label>
              <div style={{ display: 'flex', gap: spacing.sm }}>
                {[
                  { value: 'unpaid', label: 'Not paid' },
                  { value: 'partial', label: 'Part payment' },
                  { value: 'paid', label: 'Paid in full' },
                ].map((option) => {
                  const active = formStatus === option.value;
                  return (
                    <button
                      key={option.value}
                      type='button'
                      onClick={() => {
                        // These buttons are shortcuts for the "Amount Paid" field.
                        if (option.value === 'paid') {
                          setField('paidAmount', formAmount > 0 ? String(formAmount) : '');
                        } else if (option.value === 'unpaid') {
                          setField('paidAmount', '');
                        }
                      }}
                      style={{
                        flex: 1,
                        padding: spacing.sm + ' ' + spacing.xs,
                        borderRadius: '0.5rem',
                        border: '2px solid ' + (active ? TONES[option.value].border : colors.border),
                        backgroundColor: active ? TONES[option.value].background : 'white',
                        color: active ? TONES[option.value].color : colors.text.primary,
                        fontWeight: 700,
                        cursor: option.value === 'partial' ? 'default' : 'pointer',
                        fontSize: '0.82rem',
                      }}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
              <p style={{ color: colors.text.secondary, fontSize: '0.78rem', marginTop: spacing.xs, marginBottom: 0 }}>
                {formStatus === 'paid'
                  ? 'Fully paid - balance ' + formatCurrency(0)
                  : 'Balance to pay ' + formatCurrency(formBalance) + ' (set the amount paid for part payments)'}
              </p>
            </div>
            ) : null}

            {!isCashBill ? (
            <div>
              <label style={labelStyle} htmlFor='pd-due-date'>
                Due Date {form.paymentStatus === 'unpaid' ? '*' : '(optional)'}
              </label>
              <input
                id='pd-due-date'
                type='date'
                value={form.dueDate}
                onChange={(event) => setField('dueDate', event.target.value)}
                style={inputStyle}
              />
              {errors.dueDate ? <p style={errorStyle}>{errors.dueDate}</p> : null}
              {!errors.dueDate && creditDays > 0 ? (
                <p style={{ color: colors.text.secondary, fontSize: '0.78rem', marginTop: spacing.xs, marginBottom: 0 }}>
                  {config.partyTitle + ' credit period: ' + creditDays + ' days - due date filled automatically (editable).'}
                </p>
              ) : null}
            </div>
            ) : null}
          </div>

          <div style={{ marginTop: spacing.md }}>
            <label style={labelStyle} htmlFor='pd-notes'>
              Notes (optional)
            </label>
            <textarea
              id='pd-notes'
              value={form.notes}
              onChange={(event) => setField('notes', event.target.value)}
              rows={3}
              placeholder='Any additional information about this invoice'
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          {serverError ? (
            <div
              style={{
                marginTop: spacing.md,
                padding: spacing.md,
                borderRadius: '0.5rem',
                backgroundColor: '#fdeaea',
                color: '#b42318',
                fontSize: '0.9rem',
                fontWeight: 600,
              }}
            >
              {serverError}
            </div>
          ) : null}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: spacing.md, marginTop: spacing.lg, flexWrap: 'wrap' }}>
            <button
              type='button'
              onClick={onClose}
              disabled={saving}
              style={{
                padding: spacing.md + ' ' + spacing.lg,
                borderRadius: '0.5rem',
                border: '2px solid ' + colors.border,
                backgroundColor: 'white',
                fontWeight: 700,
                cursor: saving ? 'not-allowed' : 'pointer',
                color: colors.text.primary,
              }}
            >
              Cancel
            </button>
            <button
              type='submit'
              disabled={saving}
              style={{
                padding: spacing.md + ' ' + spacing.lg,
                borderRadius: '0.5rem',
                border: 'none',
                backgroundColor: saving ? colors.neutral[400] : colors.primary[500],
                color: 'white',
                fontWeight: 700,
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Saving...' : (isEdit ? 'Save Changes' : (isCashBill ? 'Save Cash Bill' : config.addLabel))}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

export default FinanceInvoiceModal;




